import { Primitive } from "type-fest"

import FileUtils from "./file"

import { isRecord } from "../common"

type Plain = Primitive | Record<keyof never, unknown> | (Primitive | Record<keyof never, unknown> | unknown[])[]

class FormDataEnhanced extends FormData {
  constructor(record: Record<keyof never, Plain | Blob | Blob[]>, options?: { form?: HTMLFormElement, submitter?: HTMLElement | null }) {
    super(options?.form, options?.submitter)

    Object.entries(record).forEach(([key, value]) => {
      if (value == null) return

      if (isRecord(value)) {
        super.set(key, JSON.stringify(value))
        return
      }

      if (value instanceof Blob) {
        super.set(key, value)
        return
      }
      if (value instanceof Array && value[0] instanceof Blob) {
        this.appendMany(key, value as Blob[])
        return
      }

      super.set(key, String(value))
    })
  }

  /**
   * Renames all attached files to their hashes.
   */
  async renameFilesToHashes(): Promise<void> {
    const allKeys = [...this.keys()]
    const uniqueKeys = [...new Set(allKeys)]
    
    const fileEntriesByKey = new Map<string, Blob[]>()
    
    uniqueKeys.forEach(key => {
      const values = this.getAll(key)
      const blobs = values.filter(value => value instanceof Blob) as Blob[]
      if (blobs.length > 0) {
        fileEntriesByKey.set(key, blobs)
      }
    })

    const renamedFilePromises = Array.from(fileEntriesByKey.entries())
      .map(async ([key, blobs]) => [
        key, 
        await Promise.all(blobs.map(blob => FileUtils.renameToHash(blob)))
      ] as const)
    
    const renamedFiles = await Promise.all(renamedFilePromises)

    fileEntriesByKey.forEach((_, key) => super.delete(key))
    renamedFiles.forEach(([key, files]) => {
      files.forEach(file => super.append(key, file))
    })
  }

  async appendHashNamedFile(key: string, file: Blob) {
    super.append(key, await FileUtils.renameToHash(file))
  }
  appendMany(key: string, values: (string | Blob)[]) {
    values.forEach(value => super.append(key, value))
  }
}

export default FormDataEnhanced
